import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

// eslint-disable-next-line max-lines-per-function
const NetworkVisualizationOptions = ({ width = 800, height = 600, selectedVisualization = 'hierarchical', filteredRelationships = null, onNodeClick = null }) => {
  const svgRef = useRef();
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  // Process relationship data for D3
  const processData = () => {
    const nodes = new Map();
    const links = [];

    // Use provided relationships (no fallback to mock data)
    const relationshipsToProcess = filteredRelationships || [];

    relationshipsToProcess.forEach(rel => {
      // Add nodes
      if (!nodes.has(rel.entity1)) {
        nodes.set(rel.entity1, {
          id: rel.entity1,
          name: rel.entity1,
          group: getPersonGroup(rel.entity1),
          totalConnections: 0,
          avgTrust: 0,
          avgCollaboration: 0
        });
      }
      if (!nodes.has(rel.entity2)) {
        nodes.set(rel.entity2, {
          id: rel.entity2,
          name: rel.entity2,
          group: getPersonGroup(rel.entity2),
          totalConnections: 0,
          avgTrust: 0,
          avgCollaboration: 0
        });
      }

      // Add links
      links.push({
        source: rel.entity1,
        target: rel.entity2,
        trust: rel.trust,
        communication: rel.communication,
        psychological_safety: rel.psychological_safety,
        collaboration: rel.collaboration,
        alignment: rel.alignment,
        type: rel.relationship_type,
        notes: rel.notes,
        context: rel.context,
        evolution: rel.evolution,
        strength: (rel.trust + rel.collaboration + rel.alignment) / 3
      });
    });

    // Calculate node metrics
    const nodeArray = Array.from(nodes.values());
    nodeArray.forEach(node => {
      const nodeLinks = links.filter(l => l.source === node.id || l.target === node.id);
      node.totalConnections = nodeLinks.length;
      node.avgTrust = nodeLinks.reduce((sum, l) => sum + l.trust, 0) / nodeLinks.length || 0;
      node.avgCollaboration = nodeLinks.reduce((sum, l) => sum + l.collaboration, 0) / nodeLinks.length || 0;
    });

    return { nodes: nodeArray, links };
  };

  const getPersonGroup = (name) => {
    if (name.includes('Emily Carter') || name.includes('James Mitchell')) return 'leadership';
    if (name.includes('Chen') || name.includes('Martinez') || name.includes('Thompson') || name.includes('Patel')) return 'engineering';
    if (name.includes('Turner') || name.includes('Chang') || name.includes('Foster') || name.includes('Kim')) return 'sales';
    if (name.includes('Foster') || name.includes('Rodriguez') || name.includes('Martinez')) return 'product';
    if (name.includes('Board') || name.includes('Director') || name.includes('Kumar')) return 'board';
    return 'other';
  };

  const getNodeColor = (group, strength = 5) => {
    const baseColors = {
      leadership: '#2563eb',
      engineering: '#059669',
      sales: '#dc2626',
      product: '#7c3aed',
      board: '#ea580c',
      other: '#6b7280'
    };

    const intensity = Math.max(0.3, Math.min(1, strength / 10));
    const color = d3.color(baseColors[group]);
    color.opacity = intensity;
    return color.toString();
  };

  const showTooltip = (event, data) => {
    setTooltip({
      show: true,
      content: data,
      x: event.clientX + 10,
      y: event.clientY - 10
    });
  };

  const hideTooltip = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  // 1. Hierarchical/Organizational Chart
  const renderHierarchicalView = (svg, data) => {
    svg.selectAll('*').remove();

    // Find nodes without managers (potential roots)
    const nodesWithManagers = new Set();
    data.links.forEach(link => {
      if (link.type === 'manager_direct_report' || link.type === 'manager_indirect_report') {
        nodesWithManagers.add(link.target);
      }
    });

    const rootNodes = data.nodes.filter(node => !nodesWithManagers.has(node.id));

    // Create a virtual root if there are multiple roots or no clear hierarchy
    let hierarchyData = [...data.nodes];
    if (rootNodes.length > 1 || rootNodes.length === 0) {
      // Add virtual root
      hierarchyData.push({
        id: 'virtual-root',
        name: 'Organization',
        group: 'root',
        totalConnections: 0,
        avgTrust: 0
      });

      // Make all actual roots children of virtual root
      const actualRoots = rootNodes.length > 0 ? rootNodes : data.nodes.slice(0, 3); // Take first 3 if no clear roots
      hierarchyData = hierarchyData.map(node => {
        if (actualRoots.includes(node) || (rootNodes.length === 0 && actualRoots.includes(node))) {
          return node; // These will be connected to virtual root via modified getManagerOf
        }
        return node;
      });
    }

    try {
      const hierarchy = d3.stratify()
        .id(d => d.id)
        .parentId(d => {
          if (d.id === 'virtual-root') return null;
          const manager = getManagerOf(d.id, data.links);
          // If no manager found and we have a virtual root, connect to virtual root
          if (!manager && hierarchyData.find(n => n.id === 'virtual-root')) {
            return rootNodes.includes(d) || (rootNodes.length === 0 && data.nodes.slice(0, 3).includes(d)) ? 'virtual-root' : null;
          }
          return manager;
        })(hierarchyData);

      const tree = d3.tree().size([width - 200, height - 100]);
      const root = tree(hierarchy);

      // Links
      svg.selectAll('.link')
        .data(root.links())
        .enter()
        .append('path')
        .attr('class', 'link')
        .attr('d', d3.linkVertical()
          .x(d => d.x + 100)
          .y(d => d.y + 50))
        .style('fill', 'none')
        .style('stroke', '#999')
        .style('stroke-width', 2);

      // Nodes
      const nodeGroup = svg.selectAll('.node')
        .data(root.descendants())
        .enter()
        .append('g')
        .attr('class', 'node')
        .attr('transform', d => `translate(${d.x + 100}, ${d.y + 50})`);

      nodeGroup.append('circle')
        .attr('r', d => d.data.id === 'virtual-root' ? 8 : 5 + (d.data.totalConnections || 0))
        .style('fill', d => d.data.id === 'virtual-root' ? '#333' : getNodeColor(d.data.group, d.data.avgTrust))
        .style('stroke', '#fff')
        .style('stroke-width', 2)
        .on('mouseover', (event, d) => showTooltip(event, d.data.id === 'virtual-root' ? 'Organization Root' : `${d.data.name}\nConnections: ${d.data.totalConnections}\nAvg Trust: ${d.data.avgTrust.toFixed(1)}`))
        .on('mouseout', hideTooltip)
        .on('click', (event, d) => {
          if (onNodeClick && d.data.id !== 'virtual-root') {
            onNodeClick(d.data.name);
          }
        })
        .style('cursor', 'pointer');

      nodeGroup.append('text')
        .attr('dy', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .text(d => d.data.id === 'virtual-root' ? 'Org' : d.data.name.split(' ')[0]);

    } catch (error) {
      console.error('Error creating hierarchy:', error);
      // Fallback to circular layout if hierarchy fails
      renderCircularView(svg, data);
      return;
    }
  };

  // 2. Circular/Radial Layout
  const renderCircularView = (svg, data) => {
    svg.selectAll('*').remove();

    const radius = Math.min(width, height) / 2 - 50;
    const center = { x: width / 2, y: height / 2 };

    // Position nodes in circles by group
    const angleStep = (2 * Math.PI) / data.nodes.length;
    data.nodes.forEach((node, i) => {
      const angle = i * angleStep;
      const groupRadius = radius * (0.4 + Math.random() * 0.4);
      node.x = center.x + groupRadius * Math.cos(angle);
      node.y = center.y + groupRadius * Math.sin(angle);
    });

    // Draw links
    svg.selectAll('.link')
      .data(data.links)
      .enter()
      .append('line')
      .attr('class', 'link')
      .attr('x1', d => data.nodes.find(n => n.id === d.source).x)
      .attr('y1', d => data.nodes.find(n => n.id === d.source).y)
      .attr('x2', d => data.nodes.find(n => n.id === d.target).x)
      .attr('y2', d => data.nodes.find(n => n.id === d.target).y)
      .style('stroke', d => d.evolution === 'strengthening' ? '#059669' :
                          d.evolution === 'deteriorated' ? '#dc2626' : '#6b7280')
      .style('stroke-width', d => d.strength / 2)
      .style('opacity', 0.6);

    // Draw nodes
    svg.selectAll('.node')
      .data(data.nodes)
      .enter()
      .append('circle')
      .attr('class', 'node')
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('r', d => 8 + d.totalConnections * 2)
      .style('fill', d => getNodeColor(d.group, d.avgTrust))
      .style('stroke', '#fff')
      .style('stroke-width', 2)
      .on('mouseover', (event, d) => showTooltip(event, `${d.name}\nGroup: ${d.group}\nTrust: ${d.avgTrust.toFixed(1)}`))
      .on('mouseout', hideTooltip)
      .on('click', (event, d) => {
        if (onNodeClick) {
          onNodeClick(d.name);
        }
      })
      .style('cursor', 'pointer');

    // Add labels
    svg.selectAll('.label')
      .data(data.nodes)
      .enter()
      .append('text')
      .attr('class', 'label')
      .attr('x', d => d.x)
      .attr('y', d => d.y + 25)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('fill', '#333')
      .text(d => d.name.split(' ')[0]);
  };

  // 3. Matrix/Heatmap View
  const renderMatrixView = (svg, data) => {
    svg.selectAll('*').remove();

    const margin = { top: 50, right: 50, bottom: 100, left: 100 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const nodes = data.nodes.slice(0, 20); // Limit for readability
    const cellSize = Math.min(innerWidth / nodes.length, innerHeight / nodes.length);

    // Create scales
    const xScale = d3.scaleBand()
      .domain(nodes.map(d => d.id))
      .range([0, nodes.length * cellSize]);

    const yScale = d3.scaleBand()
      .domain(nodes.map(d => d.id))
      .range([0, nodes.length * cellSize]);

    const colorScale = d3.scaleSequential(d3.interpolateRdYlGn)
      .domain([1, 10]);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Create matrix data
    const matrixData = [];
    nodes.forEach(source => {
      nodes.forEach(target => {
        const link = data.links.find(l =>
          (l.source === source.id && l.target === target.id) ||
          (l.source === target.id && l.target === source.id)
        );
        matrixData.push({
          source: source.id,
          target: target.id,
          value: link ? link.trust : 0,
          relationship: link ? link.type : 'none'
        });
      });
    });

    // Draw cells
    g.selectAll('.cell')
      .data(matrixData)
      .enter()
      .append('rect')
      .attr('class', 'cell')
      .attr('x', d => xScale(d.source))
      .attr('y', d => yScale(d.target))
      .attr('width', cellSize - 1)
      .attr('height', cellSize - 1)
      .style('fill', d => d.value > 0 ? colorScale(d.value) : '#f5f5f5')
      .style('stroke', '#fff')
      .on('mouseover', (event, d) => showTooltip(event, `${d.source} → ${d.target}\nTrust: ${d.value}\nType: ${d.relationship}`))
      .on('mouseout', hideTooltip);

    // Add axes
    g.append('g')
      .selectAll('.x-label')
      .data(nodes)
      .enter()
      .append('text')
      .attr('class', 'x-label')
      .attr('x', d => xScale(d.id) + cellSize / 2)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .text(d => d.name.split(' ')[0])
      .attr('transform', d => `rotate(-45, ${xScale(d.id) + cellSize / 2}, -10)`);

    g.append('g')
      .selectAll('.y-label')
      .data(nodes)
      .enter()
      .append('text')
      .attr('class', 'y-label')
      .attr('x', -10)
      .attr('y', d => yScale(d.id) + cellSize / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-size', '10px')
      .text(d => d.name.split(' ')[0]);
  };

  // 4. Network Clusters View
  const renderClustersView = (svg, data) => {
    svg.selectAll('*').remove();

    const clusterCenters = {
      leadership: { x: width * 0.2, y: height * 0.2 },
      engineering: { x: width * 0.8, y: height * 0.3 },
      sales: { x: width * 0.3, y: height * 0.8 },
      product: { x: width * 0.7, y: height * 0.7 },
      board: { x: width * 0.5, y: height * 0.1 },
      other: { x: width * 0.5, y: height * 0.5 }
    };

    // Position nodes around cluster centers
    data.nodes.forEach(node => {
      const center = clusterCenters[node.group];
      const angle = Math.random() * 2 * Math.PI;
      const radius = 30 + Math.random() * 50;
      node.x = center.x + radius * Math.cos(angle);
      node.y = center.y + radius * Math.sin(angle);
    });

    // Draw cluster backgrounds
    Object.entries(clusterCenters).forEach(([group, center]) => {
      svg.append('circle')
        .attr('cx', center.x)
        .attr('cy', center.y)
        .attr('r', 80)
        .style('fill', getNodeColor(group, 3))
        .style('opacity', 0.1)
        .style('stroke', getNodeColor(group, 7))
        .style('stroke-width', 2)
        .style('stroke-dasharray', '5,5');

      svg.append('text')
        .attr('x', center.x)
        .attr('y', center.y - 60)
        .attr('text-anchor', 'middle')
        .style('font-size', '14px')
        .style('font-weight', 'bold')
        .style('fill', getNodeColor(group, 8))
        .text(group.toUpperCase());
    });

    // Draw links
    svg.selectAll('.link')
      .data(data.links)
      .enter()
      .append('line')
      .attr('class', 'link')
      .attr('x1', d => data.nodes.find(n => n.id === d.source).x)
      .attr('y1', d => data.nodes.find(n => n.id === d.source).y)
      .attr('x2', d => data.nodes.find(n => n.id === d.target).x)
      .attr('y2', d => data.nodes.find(n => n.id === d.target).y)
      .style('stroke', d => {
        const sourceNode = data.nodes.find(n => n.id === d.source);
        const targetNode = data.nodes.find(n => n.id === d.target);
        return sourceNode.group === targetNode.group ? '#059669' : '#6b7280';
      })
      .style('stroke-width', d => d.strength / 3)
      .style('opacity', 0.6);

    // Draw nodes
    svg.selectAll('.node')
      .data(data.nodes)
      .enter()
      .append('circle')
      .attr('class', 'node')
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('r', d => 6 + d.totalConnections)
      .style('fill', d => getNodeColor(d.group, d.avgTrust))
      .style('stroke', '#fff')
      .style('stroke-width', 2)
      .on('mouseover', (event, d) => showTooltip(event, `${d.name}\nGroup: ${d.group}\nConnections: ${d.totalConnections}`))
      .on('mouseout', hideTooltip)
      .on('click', (event, d) => {
        if (onNodeClick) {
          onNodeClick(d.name);
        }
      })
      .style('cursor', 'pointer');

    // Add labels
    svg.selectAll('.label')
      .data(data.nodes)
      .enter()
      .append('text')
      .attr('class', 'label')
      .attr('x', d => d.x)
      .attr('y', d => d.y + 20)
      .attr('text-anchor', 'middle')
      .style('font-size', '9px')
      .style('fill', '#333')
      .text(d => d.name.split(' ')[0]);
  };

  const getManagerOf = (personId, links) => {
    const managerLink = links.find(l =>
      l.target === personId &&
      (l.type === 'manager_direct_report' || l.type === 'manager_indirect_report')
    );
    return managerLink ? managerLink.source : null;
  };

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const data = processData();

    switch (selectedVisualization) {
      case 'hierarchical':
        renderHierarchicalView(svg, data);
        break;
      case 'circular':
        renderCircularView(svg, data);
        break;
      case 'matrix':
        renderMatrixView(svg, data);
        break;
      case 'clusters':
        renderClustersView(svg, data);
        break;
      default:
        renderCircularView(svg, data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVisualization, width, height, filteredRelationships]);

  return (
    <div className="network-visualization">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
      />

      {tooltip.show && (
        <div
          className="tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            pointerEvents: 'none',
            zIndex: 1000,
            whiteSpace: 'pre-line'
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default NetworkVisualizationOptions;